// frontend/src/hooks/useRecordRouteStop.ts
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { readGrantMirror } from '../lib/grantMirror'
import { getRouteOutcomes, localDateKey, saveRouteOutcomes } from '../lib/offlineStore'
import type { QueuedRouteOutcome } from '../lib/offlineStore'
import { syncEngine } from '../lib/syncEngine'
import { uuid } from '../lib/uuid'
import type { RouteOutcome } from '../types/canvasser'
import type { DoorSubmitResult } from './useRecordDoor'

export interface RouteStopSubmission {
  turfId: string
  contactIds: string[]
  outcome: RouteOutcome
  reason?: string | null
}

/**
 * [CF-1 P3] Records one action-route stop ("Dropped off" / "Couldn't") for every contact on it. The work
 * is SAVED BEFORE it is sent (so it survives a dead network or a closed tab), then drained if online.
 * The result is read back from the store: synced = saved, rejected = the server's own message,
 * anything else = queued for the sync engine. Ids are minted once here and never change on retry.
 */
export async function submitRouteStop(s: RouteStopSubmission): Promise<DoorSubmitResult> {
  const clientTimestamp = new Date().toISOString()
  const localDate = localDateKey()
  // [R5] Stamp the recording grant so a "Canvass this" switch never sends it as another grant.
  const grantId = readGrantMirror()?.grantId
  const queued: QueuedRouteOutcome[] = s.contactIds.map(contactId => {
    const clientOutcomeId = uuid()
    return {
      clientOutcomeId,
      outcome: { clientOutcomeId, turfId: s.turfId, contactId, outcome: s.outcome, reason: s.reason ?? null, clientTimestamp },
      localDate, synced: false, retryCount: 0,
      ...(grantId ? { grantId } : {}),
    }
  })
  await saveRouteOutcomes(queued)
  if (navigator.onLine) {
    try { await syncEngine.drainRouteOutcomes() } catch { /* stays queued; the next sync drains it */ }
  }
  const ids = new Set(queued.map(q => q.clientOutcomeId))
  const now = (await getRouteOutcomes(localDate)).filter(o => ids.has(o.clientOutcomeId))
  const out: DoorSubmitResult = { saved: 0, queued: 0, rejected: [] }
  for (const o of now) {
    if (o.rejected) out.rejected.push({ contactId: o.outcome.contactId, message: o.rejectedReason ?? 'This drop-off could not be saved' })
    else if (o.synced) out.saved++
    else out.queued++
  }
  await syncEngine.refreshPendingCount()
  return out
}

export function useRecordRouteStop() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: submitRouteStop,
    // submitRouteStop handles offline itself (queue); TanStack's default 'online' would PAUSE it offline.
    networkMode: 'always',
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['canvasser', 'turfs'] })
    },
  })
}
